import { ITheme } from '@fluentui/react/lib/Styling';
import { memoizeFunction } from '@fluentui/react/lib/Utilities';
import { getStandardControlSemanticColorsWithTheme, StandardControlSemanticColors } from '../../../../../styling';
import { isThemingEnabled } from '@ms/acui-theme/lib/ThemingExperiment';

const ringWidth = 2;
const ringPadding = 2;
const ribbonCoinSize = 20;
const overflowCoinSize = 24;
const ribbonCoinSizeFull = ribbonCoinSize + 2 * (ringPadding + ringWidth);
const overflowCoinSizeFull = overflowCoinSize + 2 * (ringPadding + ringWidth);
const memberPadding = 2;
const overflowButtonMargin = 4;

export interface CoauthorGalleryPalette {
  /**
   * Overflow Menu Constants
   */
  itemBackgroundColor: string;
  itemBackgroundHoverFocusColor: string;
  itemBackgroundPressedColor: string;
  itemDisabledTextColor: string;
  itemHeight: number;
  itemSidePadding: number;

  actionButtonHeight: number;

  primaryTextColor: string;
  primaryTextSize: 14;
  secondaryTextColor: string;
  secondaryTextSize: 12;

  borderColor: string;
  focusOutlineColor: string;

  overflowExpandedColor: string;
  actionHubCalloutGap: number;
  overflowCalloutGap: number;

  maxCalloutWidth: number;
  minCalloutWidth: number;
  inactiveOpacity: number;

  /**
   * Coin Constants
   */
  ringWidth: number;
  ringPadding: number;
  ribbonCoinSize: number;
  ribbonCoinSizeFull: number;
  overflowCoinSize: number;
  overflowCoinSizeFull: number;

  /**
   * Facepile Constants
   */
  memberPadding: number;
  memberSize: number;
  overflowButtonMargin: number;
  overflowButtonSize: number;
  overflowInitialsIconColor: string;
}

export const defaultCoauthorGalleryPalette = memoizeFunction(
  (theme: ITheme): CoauthorGalleryPalette => {
    const semanticColors: StandardControlSemanticColors = getStandardControlSemanticColorsWithTheme(theme);
    return {
      /**
       * Overflow Menu Constants
       */
      itemBackgroundColor: semanticColors.menuBackgroundColor,
      itemBackgroundHoverFocusColor: semanticColors.hoverBackgroundColor,
      itemBackgroundPressedColor: semanticColors.selectedBackgroundColor,
      itemDisabledTextColor: isThemingEnabled() ? semanticColors.textDisabledColor : '#B3BOAD',
      itemHeight: 42,
      itemSidePadding: 12,

      actionButtonHeight: 32,

      primaryTextColor: semanticColors.textColor,
      primaryTextSize: 14,
      secondaryTextColor: isThemingEnabled() ? semanticColors.textSecondaryColor : '#484644',
      secondaryTextSize: 12,

      borderColor: semanticColors.borderColor,
      focusOutlineColor: isThemingEnabled() ? semanticColors.peripheralFocusedOutlineColor : '#605E5C',

      overflowExpandedColor: semanticColors.peripheralSelectedBackgroundColor,
      actionHubCalloutGap: -3,
      overflowCalloutGap: 0,

      maxCalloutWidth: 310,
      minCalloutWidth: 200,
      inactiveOpacity: 0.5,

      /**
       * Coin Constants
       */
      ringWidth,
      ringPadding,
      ribbonCoinSize,
      ribbonCoinSizeFull,
      overflowCoinSize,
      overflowCoinSizeFull,

      /**
       * Facepile Constants
       */
      memberPadding,
      memberSize: 2 * memberPadding + ribbonCoinSizeFull,
      overflowButtonMargin,
      overflowButtonSize: 2 * overflowButtonMargin + ribbonCoinSizeFull,
      overflowInitialsIconColor: semanticColors.textColor,
    };
  }
);

export const getCoauthorGalleryPalette = memoizeFunction(
  (theme: ITheme, palette?: Partial<CoauthorGalleryPalette>): CoauthorGalleryPalette => {
    return { ...defaultCoauthorGalleryPalette(theme), ...palette };
  }
);
